import { useMemo, type ReactNode } from "react";
import { useLoaderData } from "@remix-run/react";
import { ApplyWithSeekContext } from "./contexts/apply_with_seek_context";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import type { SeekLoaderData } from "./utils/seek_loader.server";

interface ApplyWithSeekProps {
  children: ReactNode;
}

/**
 * Type for loader data that includes Seek data and optionally job post ID.
 * This component can be used in any route that includes Seek data in its loader.
 */
type LoaderDataWithSeek = {
  seek: SeekLoaderData["seek"];
  jobPostId?: string | number;
};

export const ApplyWithSeek = ({ children }: ApplyWithSeekProps) => {
  const { seek, jobPostId } = useLoaderData<LoaderDataWithSeek>();
  const { seek_id } = useBoardConfiguration();

  const contextValue = useMemo(
    () =>
      Object.freeze({
        applyWithSeekEnabled: seek.applyWithSeekEnabled || false,
        seekId: seek_id,
        jobPostUrl: seek.seekAuthUrl || "",
        jobPostId: jobPostId ? String(jobPostId) : undefined,
        isConfigured: Boolean(seek_id && seek.applyWithSeekEnabled),
      }),
    [seek.applyWithSeekEnabled, seek_id, seek.seekAuthUrl, jobPostId]
  );

  return (
    <ApplyWithSeekContext.Provider value={contextValue}>{children}</ApplyWithSeekContext.Provider>
  );
};
